import { useEffect, useState, type ReactNode } from "react";
import { Moon, Sun, Monitor } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTenantContextSafe } from "@/lib/tenant-context";
import { clientRequestHeaders } from "@/lib/sm-api";

type Theme = "dark" | "light" | "system";

function applyTheme(theme: Theme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.remove("light");
  } else if (theme === "light") {
    root.classList.add("light");
  } else {
    const prefersLight = window.matchMedia("(prefers-color-scheme: light)").matches;
    root.classList.toggle("light", prefersLight);
  }
}

export function ThemeToggle() {
  const ctx = useTenantContextSafe();
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window === "undefined") return "dark";
    return (localStorage.getItem("sm_theme") as Theme) ?? "dark";
  });

  useEffect(() => {
    if (ctx?.themeMode) setTheme(ctx.themeMode);
  }, [ctx?.themeMode]);

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem("sm_theme", theme);
  }, [theme]);

  useEffect(() => {
    const onExternal = (e: Event) => {
      const detail = (e as CustomEvent<Theme>).detail;
      if (detail === "dark" || detail === "light" || detail === "system") setTheme(detail);
    };
    window.addEventListener("sm-theme-changed", onExternal);
    return () => window.removeEventListener("sm-theme-changed", onExternal);
  }, []);

  useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const handler = () => applyTheme("system");
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [theme]);

  const persist = (nextTheme: Theme) => {
    setTheme(nextTheme);
    void fetch("/api/auth/profile", {
      method: "PATCH",
      credentials: "include",
      headers: { ...clientRequestHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ themeMode: nextTheme }),
    }).catch(() => undefined);
  };

  const next: Record<Theme, Theme> = { dark: "light", light: "system", system: "dark" };
  const icons: Record<Theme, ReactNode> = {
    dark: <Moon className="h-4 w-4" />,
    light: <Sun className="h-4 w-4" />,
    system: <Monitor className="h-4 w-4" />,
  };
  const labels: Record<Theme, string> = {
    dark: "Modo oscuro",
    light: "Modo claro",
    system: "Modo sistema",
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-foreground"
            onClick={() => persist(next[theme])}
            aria-label={labels[theme]}
          >
            {icons[theme]}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">{labels[theme]}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
